import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileImage } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { extractSopValues, validateImportedValues } from '@/components/sop-intake/lib/file-import';
import type { FormValues } from '@/lib/types';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import { useToast } from '@/hooks/use-toast';
import logger from '@/lib/logging';
import {
  assertSopSheetDataUriSize,
  isAcceptedSopSheetFile,
  SOP_SHEET_FILE_ACCEPT,
} from '@/lib/sop-intake/sop-sheet-upload';

export interface SopSheetImportParams {
  experimentalFeaturesEnabled: boolean;
  formValues: FormValues;
  onInputChange: (id: string, value: string) => void;
  testMode: 'passive' | 'active';
  ruleDocVersion: RuleDocVersion;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read sheet file'));
    reader.readAsDataURL(file);
  });
}

async function clipboardItemToFile(item: DataTransferItem): Promise<File | null> {
  return item.getAsFile();
}

/**
 * Shared logic for experimental SOP sheet image import (API + file + paste).
 * Call once per page; use two {@link SopSheetImportButton}s with the returned ref if needed.
 */
export function useSopSheetImport({
  experimentalFeaturesEnabled,
  formValues,
  onInputChange,
  testMode,
  ruleDocVersion,
}: SopSheetImportParams) {
  const { toast } = useToast();
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parseAndApply = useCallback(
    async (imageDataUri: string) => {
      try {
        assertSopSheetDataUriSize(imageDataUri);
      } catch {
        toast({
          title: 'File too large',
          description: 'Use a smaller image or PDF (max ~14MB).',
          variant: 'destructive',
        });
        return;
      }

      setIsParsing(true);
      try {
        const result = await apiFetch<{ values: Record<string, unknown> }>('/assessments/parse-sop-sheet', {
          method: 'POST',
          body: JSON.stringify({
            imageDataUri,
            testMode,
            ruleDocVersion,
          }),
        });

        const validKeys = Object.keys(formValues);
        const imported = extractSopValues(result.values ?? {}, validKeys);
        const validation = validateImportedValues(imported);
        if (!validation.valid) {
          toast({
            title: 'Could not apply sheet data',
            description: validation.errors.join('; '),
            variant: 'destructive',
          });
          return;
        }

        Object.entries(imported).forEach(([key, value]) => {
          onInputChange(key, String(value));
        });

        toast({
          title: 'Sheet import applied',
          description: `Updated ${Object.keys(imported).length} field(s) from the sheet.`,
        });
      } catch (error) {
        logger.error('SOP sheet import failed', error);
        toast({
          title: 'Sheet import failed',
          description: error instanceof Error ? error.message : 'Unknown error',
          variant: 'destructive',
        });
      } finally {
        setIsParsing(false);
      }
    },
    [formValues, onInputChange, ruleDocVersion, testMode, toast]
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isAcceptedSopSheetFile(file)) {
      toast({
        title: 'Invalid file',
        description: 'Choose an image (PNG, JPEG, etc.) or a PDF.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const dataUri = await readFileAsDataUri(file);
      await parseAndApply(dataUri);
    } catch (error) {
      logger.error('SOP sheet file import failed', error);
      toast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Failed to read file',
        variant: 'destructive',
      });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    enabled: experimentalFeaturesEnabled,
    isParsing,
    fileInputRef,
    handleFileChange,
    parseAndApply,
    triggerFilePicker,
  };
}

/** Register once per page: paste image when focus is not in an editable field. */
export function SopSheetImportPasteListener({
  enabled,
  parseAndApply,
}: {
  enabled: boolean;
  parseAndApply: (imageDataUri: string) => Promise<void>;
}) {
  const { toast } = useToast();

  useEffect(() => {
    if (!enabled) return;

    const onPaste = (event: ClipboardEvent) => {
      if (isEditableTarget(event.target)) return;
      const items = event.clipboardData?.items;
      if (!items?.length) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          event.preventDefault();
          void (async () => {
            try {
              const file = await clipboardItemToFile(item);
              if (!file || !file.type.startsWith('image/')) return;
              const dataUri = await readFileAsDataUri(file);
              await parseAndApply(dataUri);
            } catch (err) {
              logger.error('SOP sheet paste handling failed', err);
              toast({
                title: 'Paste failed',
                description: err instanceof Error ? err.message : 'Could not read clipboard image',
                variant: 'destructive',
              });
            }
          })();
          return;
        }
      }
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [enabled, parseAndApply, toast]);

  return null;
}

export function SopSheetImportHiddenFileInput({
  fileInputRef,
  onChange,
}: {
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <input
      ref={fileInputRef}
      type="file"
      accept={SOP_SHEET_FILE_ACCEPT}
      className="hidden"
      aria-hidden
      onChange={onChange}
    />
  );
}

export function SopSheetImportButton({
  triggerFilePicker,
  isParsing,
  disabled = false,
  className,
}: {
  triggerFilePicker: () => void;
  isParsing: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const label = isParsing
    ? 'Reading sheet from file'
    : 'Import SOP sheet from image or PDF (experimental AI). You can also paste a screenshot with Ctrl+V or Command+V when not typing in a field.';
  return (
    <Button
      type="button"
      variant="outline"
      className={className ?? 'h-10'}
      disabled={disabled || isParsing}
      onClick={triggerFilePicker}
      title="Import a screenshot or PDF of a filled SOP sheet (experimental AI). Or paste an image with Ctrl+V (Windows/Linux) or ⌘V (Mac) when focus is not in a text field."
      aria-label={label}
    >
      <FileImage className="h-4 w-4 sm:mr-2 shrink-0" aria-hidden />
      <span className="hidden sm:inline text-left">
        {isParsing ? (
          'Reading sheet…'
        ) : (
          <>
            Sheet (AI)
            <span className="text-muted-foreground font-normal" aria-hidden>
              {' '}
              ·{' '}
            </span>
            <span className="text-[11px] font-normal text-muted-foreground">Ctrl+V / ⌘V</span>
          </>
        )}
      </span>
    </Button>
  );
}

export interface SopSheetImageImportProps extends SopSheetImportParams {
  disabled?: boolean;
}

/**
 * Experimental: one control = hidden file input + button + paste listener (single instance).
 * Prefer {@link useSopSheetImport} + {@link SopSheetImportPasteListener} + two {@link SopSheetImportButton}s when you need the same import on desktop and mobile toolbars.
 */
export default function SopSheetImageImport({
  experimentalFeaturesEnabled,
  formValues,
  onInputChange,
  testMode,
  ruleDocVersion,
  disabled = false,
}: SopSheetImageImportProps) {
  const { enabled, isParsing, fileInputRef, handleFileChange, parseAndApply, triggerFilePicker } = useSopSheetImport({
    experimentalFeaturesEnabled,
    formValues,
    onInputChange,
    testMode,
    ruleDocVersion,
  });

  if (!enabled) {
    return null;
  }

  return (
    <>
      <SopSheetImportPasteListener enabled parseAndApply={parseAndApply} />
      <SopSheetImportHiddenFileInput fileInputRef={fileInputRef} onChange={handleFileChange} />
      <SopSheetImportButton triggerFilePicker={triggerFilePicker} isParsing={isParsing} disabled={disabled} />
    </>
  );
}
